import * as React from 'react';
import { ActionButton, Space, message } from '@ui';
import { t } from '@lib/i18n';

/**
 * 密钥/令牌类敏感值的统一展示单元格
 *
 * 安装令牌与堡垒密钥共用，保证两处的脱敏显示、显示/隐藏切换与复制行为完全一致。
 *
 * 行为：
 *   - 默认脱敏显示（前 head 位 + 掩码 + 后 tail 位）
 *   - 「显示/隐藏」按钮切换明文（状态按行独立，由组件实例自行持有）
 *   - 「复制」按钮复制的是完整原文，与当前是否展开无关
 */

export interface MaskedValueCellProps {
  /** 原始完整值（复制时使用） */
  value: string;
  /** 脱敏时保留的前缀长度 */
  head?: number;
  /** 脱敏时保留的后缀长度 */
  tail?: number;
}

export function maskValue(v: string, head = 6, tail = 4): string {
  if (!v || v.length <= 12) return v;
  return `${v.slice(0, head)}••••••••${v.slice(-tail)}`;
}

export const MaskedValueCell: React.FC<MaskedValueCellProps> = ({
  value,
  head = 6,
  tail = 4,
}) => {
  const [revealed, setRevealed] = React.useState(false);

  const copy = () => {
    navigator.clipboard.writeText(value).then(
      () => message.success(t('common.copied')),
      () => message.error(t('common.copyFailed')),
    );
  };

  if (!value) return <span>-</span>;

  return (
    <Space size="small" wrap>
      <span className="font-mono text-xs break-all">
        {revealed ? value : maskValue(value, head, tail)}
      </span>
      <ActionButton action={revealed ? 'hide' : 'show'} onClick={() => setRevealed((p) => !p)} />
      <ActionButton action="copy" onClick={copy} />
    </Space>
  );
};
